import { useEffect } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { createGlobalHeaders, resolveBaseUrl } from 'v2/shared/services/create-service'

import { FLOW_ENDPOINTS } from 'v2/shared/services/flow-service'
import { EvalEvents, evalEventBus } from 'v2/utils/event-bus/eval-events'
import { agentLogger } from 'v2/utils/logger'
import { useVibeEvalStore } from '../../vibe-agent/eval/eval-store'
import type { ResultsTreeStateSnapshot } from '../eval-interface/results-tree'
import { useEvalStore } from '../store'

export const useEvalEvents = () => {
  useEffect(() => {
    const persistSnapshotForCurrentEval = (options?: { markAsCancelled?: boolean }) => {
      const evalState = useEvalStore.getState()
      const evalId = evalState.currentEvalId

      if (!evalId) return
      // Only persist if there are actual nodes to save
      // This prevents overwriting a previous eval's snapshot with empty results
      if (evalState.evalResults.nodes.length === 0) {
        return
      }

      const snapshot: ResultsTreeStateSnapshot = {
        evalResults: structuredClone(evalState.evalResults),
        events: structuredClone(evalState.events),
        currentRunId: evalState.currentRunId,
        isEvalRunning: evalState.isEvalRunning,
        isPaused: evalState.isPaused,
        currentlyTestingNodeUuid: evalState.currentlyTestingNodeUuid,
      }

      if (options?.markAsCancelled) {
        snapshot.isPaused = true
        snapshot.currentlyTestingNodeUuid = null
        snapshot.isEvalRunning = false
      }

      useVibeEvalStore
        .getState()
        .persistEvalSnapshot(evalId, evalState.currentRunId ?? undefined, snapshot)
    }

    const unbinds = [
      evalEventBus.on(EvalEvents.STARTED, (payload: unknown) => {
        const currentState = useEvalStore.getState()
        const nextEvalId =
          payload && typeof payload === 'object' && 'eval_id' in payload
            ? (payload as { eval_id: string }).eval_id
            : null

        // Handle previous eval state when starting a new eval
        if (currentState.currentEvalId) {
          // If previous eval was still running, persist its snapshot first
          if (currentState.isEvalRunning) {
            persistSnapshotForCurrentEval({ markAsCancelled: true })
          }
          // Always clear the stream for a fresh start
          currentState.clearEvalStream()
        }

        const entry = {
          id: uuidv4(),
          type: EvalEvents.STARTED,
          payload,
          receivedAt: new Date().toISOString(),
        }
        useEvalStore.getState().addEvent(entry)

        // Extract and store eval_id
        if (nextEvalId) {
          useEvalStore.getState().setCurrentEvalId(nextEvalId)
        }

        useEvalStore.getState().setIsEvalRunning(true)
        useEvalStore.setState({ isPaused: false })
      }),

      ...(
        [
          EvalEvents.DATASET_GENERATION_STARTED,
          EvalEvents.DATASET_GENERATION_STARTED_NODE,
          EvalEvents.DATASET_GENERATION_COMPLETED,
          EvalEvents.DATASET_GENERATION_TEST_CASE,
          EvalEvents.TESTS_STARTED_FOR_NODE,
          EvalEvents.RESULT,
          EvalEvents.NODE_EVALUATION_SKIPPED,
        ] as const
      ).map((eventName) =>
        evalEventBus.on(eventName, (payload: unknown) => {
          useEvalStore.getState().addEvent({
            id: uuidv4(),
            type: eventName,
            payload,
            receivedAt: new Date().toISOString(),
          })
        }),
      ),

      evalEventBus.on(EvalEvents.COMPLETED, (payload: unknown) => {
        useEvalStore.getState().addEvent({
          id: uuidv4(),
          type: EvalEvents.COMPLETED,
          payload,
          receivedAt: new Date().toISOString(),
        })
        useEvalStore.getState().setIsEvalRunning(false)
        // Only persist if there are actual results to save
        const evalState = useEvalStore.getState()
        if (evalState.evalResults.nodes.length > 0) {
          persistSnapshotForCurrentEval()
        }
      }),

      evalEventBus.on(EvalEvents.CANCELLED, (payload: unknown) => {
        const entry = {
          id: uuidv4(),
          type: EvalEvents.CANCELLED,
          payload,
          receivedAt: new Date().toISOString(),
        }
        useEvalStore.getState().addEvent(entry)
        agentLogger.info('Eval cancelled by backend', { payload })
        // Keep currentEvalId and currentRunId so we can filter old events from previous runs
        // Set isPaused to true so "Resume" button shows up to allow user to resume the cancelled eval
        useEvalStore.getState().setIsEvalRunning(false)
        useEvalStore.setState({ isPaused: true })
        // Only persist if there are actual results to save
        const evalState = useEvalStore.getState()
        if (evalState.evalResults.nodes.length > 0) {
          persistSnapshotForCurrentEval()
        }
      }),

      evalEventBus.on(EvalEvents.ERROR, (payload: unknown) => {
        useEvalStore.getState().addEvent({
          id: uuidv4(),
          type: EvalEvents.ERROR,
          payload,
          receivedAt: new Date().toISOString(),
        })
        useEvalStore.getState().setIsEvalRunning(false)
        // Only persist if there are actual results to save
        const evalState = useEvalStore.getState()
        if (evalState.evalResults.nodes.length > 0) {
          persistSnapshotForCurrentEval()
        }
      }),

      evalEventBus.on(EvalEvents.STREAM_ENDED, () => {
        useEvalStore.getState().addEvent({
          id: uuidv4(),
          type: EvalEvents.STREAM_ENDED,
          payload: null,
          receivedAt: new Date().toISOString(),
        })
        useEvalStore.getState().setIsEvalRunning(false)
        // Only persist if there are actual results to save
        // This prevents overwriting a previous eval's snapshot when a new eval starts
        // (STREAM_ENDED can fire after clearEvalStream() was called by STARTED handler)
        const evalState = useEvalStore.getState()
        if (evalState.evalResults.nodes.length > 0) {
          persistSnapshotForCurrentEval()
        }
      }),
    ]

    agentLogger.debug('Eval event listeners registered')

    return () => {
      unbinds.forEach((unbind) => unbind())
      agentLogger.debug('Eval event listeners unregistered')
    }
  }, [])

  // Abort on hard refresh / tab close
  useEffect(() => {
    const handleUnload = () => {
      const evalState = useEvalStore.getState()
      if (evalState.isEvalRunning && evalState.currentEvalId) {
        agentLogger.info('Cancelling eval on page unload', { evalId: evalState.currentEvalId })

        // Abort SSE stream if active
        if (evalState.sseController) {
          try {
            evalState.sseController.abort()
            agentLogger.info('SSE stream aborted on page unload', {
              evalId: evalState.currentEvalId,
            })
          } catch (error: unknown) {
            agentLogger.error('Failed to abort SSE stream on page unload', {
              evalId: evalState.currentEvalId,
              error,
            })
          }
        }

        // Use fetch with keepalive for reliable cancellation on tab close
        // This ensures the request completes even after page unloads
        try {
          let baseURL = resolveBaseUrl(true)
          // Remove trailing slash if it exists, since FLOW_ENDPOINTS.cancelEval starts with '/'
          baseURL = baseURL.replace(/\/$/, '')
          const url = `${baseURL}${FLOW_ENDPOINTS.cancelEval}`
          const headers = createGlobalHeaders()

          fetch(url, {
            method: 'POST',
            headers,
            body: JSON.stringify({ eval_id: evalState.currentEvalId }),
            keepalive: true, // Critical: allows request to complete after page unload
          }).catch((error: unknown) => {
            // Log but don't throw - page is unloading anyway
            agentLogger.info('Failed to cancel backend eval on page unload', {
              evalId: evalState.currentEvalId,
              error,
            })
          })
        } catch (error: unknown) {
          // Handle errors like missing region gracefully
          agentLogger.info('Error setting up eval cancellation on page unload', {
            evalId: evalState.currentEvalId,
            error,
          })
        }
      }
    }

    window.addEventListener('beforeunload', handleUnload)
    return () => window.removeEventListener('beforeunload', handleUnload)
  }, [])
}
